import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Trash2, Loader2, MoreHorizontal, Printer, Paperclip, Clock, Ban, Package, Unlink, Sparkles, FileText } from "lucide-react";
import { useCan } from "@/hooks/useCan";
import { HistoryModal } from "./HistoryModal";
import { DetailDensityDropdown } from "@/features/common/components/DetailDensityDropdown";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { useSmartDeleteFlow } from "@/features/common/api/useSmartDeleteFlow";
import { SmartArchiveDialog, ChoiceDialog, PermanentDeleteConfirm } from "@/features/common/components/archive-delete";
import { routes } from "@/lib/config/routes";

interface HeaderActionsProps {
  onSave: () => void;
  /** Called for permanent deletion (after smart safety check). useDeleteItem handles toast + navigation. */
  onPermanentDelete?: () => void;
  isPermanentDeleting?: boolean;
  isSaving: boolean;
  isDirty: boolean;
  isNew?: boolean;
  itemName?: string;
  doctype?: string;
  deleteLabel?: string;
  attachmentCount?: number;
  onAttachments?: () => void;
  // Workflow props (all optional for backward compat)
  docstatus?: number;
  isSubmittable?: boolean;
  canSubmit?: boolean;
  canCancel?: boolean;
  canAmend?: boolean;
  onDocSubmit?: () => void;
  onDocCancel?: () => void;
  onDocAmend?: () => void;
  isSubmitting?: boolean;
  isCancelling?: boolean;
  isAmending?: boolean;
  /** Hide the density dropdown (e.g. in panel/slide-over mode) */
  hideDetailDensity?: boolean;
  /** Whether all required fields are filled (new items only) */
  allRequiredFilled?: boolean;
  /** Bundle-specific overflow actions (Plan 171, Epic 04) */
  isBundle?: boolean;
  onConvertToBundle?: () => void;
  isConvertingToBundle?: boolean;
  onRemoveBundle?: () => void;
  isRemovingBundle?: boolean;
}

/**
 * Header actions — Shopify-style: [Print] [⋯] [Save] [Submit]
 *
 * For submittable doctypes, also shows:
 * - Submit button (draft, docstatus=0)
 * - Cancel in overflow menu (submitted, docstatus=1)
 * - Amend button (cancelled, docstatus=2)
 */
export function HeaderActions({
  onSave,
  onPermanentDelete,
  isPermanentDeleting = false,
  isSaving,
  isDirty,
  isNew = false,
  itemName,
  doctype = "Item",
  deleteLabel = "item",
  attachmentCount = 0,
  onAttachments,
  docstatus,
  isSubmittable,
  canSubmit,
  canCancel,
  canAmend,
  onDocSubmit,
  onDocCancel,
  onDocAmend,
  isSubmitting,
  isCancelling,
  isAmending,
  hideDetailDensity,
  allRequiredFilled,
  isBundle,
  onConvertToBundle,
  isConvertingToBundle,
  onRemoveBundle,
  isRemovingBundle,
}: HeaderActionsProps) {
  const navigate = useNavigate();
  const canPrint = useCan(doctype, "print", itemName);
  const { detailDensity, setDetailDensity, detailTextSize, setDetailTextSize } = usePreferencesStore();
  const [showActionsDialog, setShowActionsDialog] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRemoveBundleDialog, setShowRemoveBundleDialog] = useState(false);

  // Smart delete flow — safety check → appropriate dialog
  const smartDelete = useSmartDeleteFlow(doctype, itemName ?? "");

  const handlePrintV1 = () => {
    if (!itemName) return;
    navigate(`/print/${encodeURIComponent(doctype)}/${encodeURIComponent(itemName)}`);
  };

  const handlePrintV2 = () => {
    if (!itemName) return;
    navigate(`/print-studio/${encodeURIComponent(doctype)}/${encodeURIComponent(itemName)}`);
  };

  // Hide Save for submitted/cancelled submittable docs
  const showSave = !(isSubmittable && (docstatus ?? 0) >= 1);
  // Show overflow if delete, cancel, or bundle actions available
  const showOverflow = !isNew && (!!onPermanentDelete || !!canCancel || !!onConvertToBundle || !!onRemoveBundle);

  return (
    <div className="flex items-center gap-2">
      {/* Density + text size — desktop only; not a mobile-touch action, and it
          clutters the compact mobile header toolbar. */}
      {!hideDetailDensity && (
        <span className="hidden md:inline-flex">
          <DetailDensityDropdown
            density={detailDensity}
            setDensity={setDetailDensity}
            textSize={detailTextSize}
            setTextSize={setDetailTextSize}
          />
        </span>
      )}

      {/* Attachments */}
      {!isNew && onAttachments && (
        <div className="relative">
          <Button
            variant="outline"
            size="icon"
            onClick={onAttachments}
            className="h-9 w-9 rounded-full bg-card border border-border"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          {attachmentCount > 0 && (
            <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-foreground text-background text-[10px] font-bold flex items-center justify-center">
              {attachmentCount}
            </span>
          )}
        </div>
      )}

      {/* Print — v1/v2 dropdown */}
      {!isNew && itemName && canPrint && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9 rounded-full bg-card border border-border"
            >
              <Printer className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={handlePrintV2}>
              <Sparkles className="w-4 h-4 mr-2 shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="font-medium">Print Studio</span>
                <span className="text-[10px] text-muted-foreground">React templates</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handlePrintV1}>
              <FileText className="w-4 h-4 mr-2 shrink-0" />
              <div className="flex flex-col min-w-0">
                <span className="font-medium">Classic Print</span>
                <span className="text-[10px] text-muted-foreground">Jinja templates</span>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* More actions (overflow) */}
      {showOverflow && (
        <>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowActionsDialog(true)}
            className="h-9 w-9 rounded-full bg-card border border-border"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>

          {/* Actions Modal */}
          <Dialog open={showActionsDialog} onOpenChange={setShowActionsDialog}>
            <DialogContent className="max-w-md p-0 flex flex-col">
              <DialogHeader className="px-10 pt-20 pb-4 !flex-row flex items-center justify-between space-y-0">
                <DialogTitle className="text-[22px] font-semibold text-foreground leading-tight tracking-[-0.2px]">
                  Actions
                </DialogTitle>
              </DialogHeader>
              <div className="px-10 pb-8 pt-2 space-y-1">
                {/* Timeline & History */}
                {itemName && (
                  <button
                    onClick={() => {
                      setShowActionsDialog(false);
                      setShowHistoryModal(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-muted transition-colors text-left group"
                  >
                    <Clock className="size-5 text-muted-foreground group-hover:scale-110 transition-transform" />
                    <div className="flex-1">
                      <div className="font-medium text-foreground">Timeline & History</div>
                      <div className="text-sm text-muted-foreground">
                        View activity log and changes
                      </div>
                    </div>
                  </button>
                )}

                {/* Convert to bundle */}
                {!isBundle && onConvertToBundle && (
                  <button
                    onClick={() => {
                      setShowActionsDialog(false);
                      onConvertToBundle();
                    }}
                    disabled={isConvertingToBundle}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-muted transition-colors text-left group"
                  >
                    <Package className="size-5 text-muted-foreground group-hover:scale-110 transition-transform" />
                    <div className="flex-1">
                      <div className="font-medium text-foreground">Convert to bundle</div>
                      <div className="text-sm text-muted-foreground">
                        Make this item a bundle of other items
                      </div>
                    </div>
                  </button>
                )}

                {/* Remove bundle */}
                {isBundle && onRemoveBundle && (
                  <button
                    onClick={() => {
                      setShowActionsDialog(false);
                      setShowRemoveBundleDialog(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-muted transition-colors text-left group"
                  >
                    <Unlink className="size-5 text-destructive group-hover:scale-110 transition-transform" />
                    <div className="flex-1">
                      <div className="font-medium text-destructive">Remove bundle</div>
                      <div className="text-sm text-muted-foreground">
                        Disconnect component items from this bundle
                      </div>
                    </div>
                  </button>
                )}

                {/* Cancel (for submitted docs) */}
                {canCancel && docstatus === 1 && (
                  <button
                    onClick={() => {
                      setShowActionsDialog(false);
                      setShowCancelDialog(true);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-muted transition-colors text-left group"
                  >
                    <Ban className="size-5 text-destructive group-hover:scale-110 transition-transform" />
                    <div className="flex-1">
                      <div className="font-medium text-destructive">Cancel</div>
                      <div className="text-sm text-muted-foreground">
                        Reverse this {deleteLabel} and undo its effects
                      </div>
                    </div>
                  </button>
                )}

                {/* Delete — smart flow: safety check → archive or delete */}
                {onPermanentDelete && (
                  <button
                    onClick={() => {
                      setShowActionsDialog(false);
                      smartDelete.initiateDelete();
                    }}
                    disabled={smartDelete.isChecking}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-md hover:bg-muted transition-colors text-left group"
                  >
                    <Trash2 className="size-5 text-destructive group-hover:scale-110 transition-transform" />
                    <div className="flex-1">
                      <div className="font-medium text-destructive">Delete</div>
                      <div className="text-sm text-muted-foreground">
                        Remove this {deleteLabel}
                      </div>
                    </div>
                  </button>
                )}
              </div>
              <DialogFooter className="px-10 pb-8">
                <Button variant="outline" onClick={() => setShowActionsDialog(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Smart Delete Dialogs */}
          {onPermanentDelete && (
            <>
              <SmartArchiveDialog
                open={smartDelete.dialogState === "smart-archive"}
                onOpenChange={(open) => { if (!open) smartDelete.closeDialogs(); }}
                itemName={itemName ?? ""}
                linkedSummary={smartDelete.safetyData?.linked_summary ?? ""}
                onArchive={() => {
                  smartDelete.archiveMutation.mutate(
                    { name: itemName ?? "" },
                    { onSuccess: () => navigate(routes.item.list) },
                  );
                }}
                isArchiving={smartDelete.archiveMutation.isPending}
              />
              <ChoiceDialog
                open={smartDelete.dialogState === "choice"}
                onOpenChange={(open) => { if (!open) smartDelete.closeDialogs(); }}
                itemName={itemName ?? ""}
                hasDisabledField={smartDelete.safetyData?.has_disabled_field ?? true}
                onArchive={() => {
                  smartDelete.archiveMutation.mutate(
                    { name: itemName ?? "" },
                    { onSuccess: () => navigate(routes.item.list) },
                  );
                }}
                onPermanentDelete={() => smartDelete.setDialogState("permanent-delete")}
                isArchiving={smartDelete.archiveMutation.isPending}
              />
              <PermanentDeleteConfirm
                open={smartDelete.dialogState === "permanent-delete"}
                onOpenChange={(open) => { if (!open) smartDelete.closeDialogs(); }}
                itemName={itemName ?? ""}
                doctype={doctype}
                onConfirm={() => {
                  onPermanentDelete();
                  // useDeleteItem handles toast + navigation
                }}
                isDeleting={isPermanentDeleting}
              />
            </>
          )}

          {/* Cancel Document Confirmation */}
          {canCancel && onDocCancel && (
            <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Cancel {doctype}?</DialogTitle>
                  <DialogDescription>
                    This will reverse the effects of this {doctype.toLowerCase()}.
                    Inventory and accounting entries will be reversed.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                    Go Back
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={isCancelling}
                    onClick={() => {
                      onDocCancel();
                      setShowCancelDialog(false);
                    }}
                  >
                    {isCancelling ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Cancelling...
                      </>
                    ) : (
                      "Cancel Document"
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Remove Bundle Confirmation */}
          {onRemoveBundle && (
            <Dialog open={showRemoveBundleDialog} onOpenChange={setShowRemoveBundleDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Remove bundle?</DialogTitle>
                  <DialogDescription>
                    This will disconnect the component items. The individual items will not be deleted.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowRemoveBundleDialog(false)} disabled={isRemovingBundle}>
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    disabled={isRemovingBundle}
                    onClick={() => {
                      onRemoveBundle();
                      setShowRemoveBundleDialog(false);
                    }}
                  >
                    {isRemovingBundle && <Loader2 className="size-4 animate-spin mr-2" />}
                    {isRemovingBundle ? "Removing..." : "Remove bundle"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}

          {/* Timeline & History Modal */}
          {itemName && (
            <HistoryModal
              open={showHistoryModal}
              onOpenChange={setShowHistoryModal}
              doctype={doctype}
              name={itemName}
            />
          )}
        </>
      )}

      {/* Save/Create — hidden for submitted/cancelled submittable docs */}
      {showSave && (
        <Button
          type="button"
          onClick={() => onSave()}
          disabled={isNew ? (!allRequiredFilled || isSaving) : (!isDirty || isSaving)}
          className={cn("rounded-full px-5", isNew && isDirty && allRequiredFilled && !isSaving && "relative")}
          aria-label={
            isNew
              ? isSaving
                ? "Creating item"
                : allRequiredFilled
                  ? isDirty ? "Create item — unsaved changes" : "Create item"
                  : "Create item — fill required fields first"
              : undefined
          }
        >
          {isSaving ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              {isNew ? "Creating\u2026" : "Saving"}
            </>
          ) : (
            isNew ? "Create" : "Save"
          )}
          {isNew && isDirty && allRequiredFilled && !isSaving && (
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-sky-400" />
          )}
        </Button>
      )}

      {/* Submit — for draft submittable docs */}
      {canSubmit && !isNew && docstatus === 0 && onDocSubmit && (
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  type="button"
                  onClick={() => setShowSubmitDialog(true)}
                  disabled={isDirty || isSubmitting}
                  className="rounded-full px-5"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            {isDirty && !isSubmitting && (
              <TooltipContent>Save changes first</TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}

      {/* Submit Confirmation */}
      {canSubmit && onDocSubmit && (
        <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Submit {doctype}?</DialogTitle>
              <DialogDescription>
                Once submitted, this {doctype.toLowerCase()} will take effect and fields
                will be locked. You can cancel it later if needed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
                Cancel
              </Button>
              <Button
                disabled={isSubmitting}
                onClick={() => {
                  onDocSubmit();
                  setShowSubmitDialog(false);
                }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Amend — for cancelled submittable docs */}
      {canAmend && docstatus === 2 && onDocAmend && (
        <Button
          type="button"
          variant="outline"
          onClick={onDocAmend}
          disabled={isAmending}
          className="rounded-full px-5"
        >
          {isAmending ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Creating amendment...
            </>
          ) : (
            "Amend"
          )}
        </Button>
      )}
    </div>
  );
}
